import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AppShell, Box, Burger, Center, Flex, Image, Indicator, NavLink, Stack, Text, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { ArrowDownward } from "@mui/icons-material";
import { useContext, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import nellyHelp from "../../assets/nelly-help.png";
import logoFull from "../../assets/therapprove-logo-transparent-white.png";
import { useDeviceType } from "../../hooks/useDeviceType";
import { Header } from "../Header";
import { AppContext, getLocalStorage, NAVBAR_BACKGROUND, setLocalStorage, useBrowserContext, useGetSubscriptionCountsQuery, useInactivityLogout, usePriorityReferralReceivedSubscription, useTherapproveModals, useWebsiteLeadReceivedSubscription } from "../Internal";
const isNavOpenKey = "isNavOpen";
export function Layout({ pages, onSessionChange, onSpotlightOpen }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { isUpToSmallScreen } = useDeviceType();
    const { isMasquerading, userRole } = useContext(AppContext);
    const { fileUpload, helpRequest } = useTherapproveModals();
    // File Drag and Drop state
    const [isDragging, setIsDragging] = useState(false);
    const dragCounter = useRef(0);
    /**
     * When any modal/drawer is open, disable the app-wide "drop anywhere to upload"
     * behavior so modal-specific dropzones (ex: FileUpload inside PriorityEntryModal)
     * can receive the drop.
     */
    const isBlockingOverlayOpen = () => {
        if (typeof document === "undefined" || typeof window === "undefined") {
            return false;
        }
        // Only treat *actual Mantine modal/drawer overlays* as blocking.
        // Avoid broad selectors like `[role="dialog"]` because popovers/comboboxes
        // can also use dialog roles and would incorrectly disable the global dropzone.
        const candidates = document.querySelectorAll([
            ".mantine-Modal-overlay",
            ".mantine-Drawer-overlay",
            ".mantine-Modal-inner",
            ".mantine-Drawer-inner"
        ].join(","));
        for (const el of Array.from(candidates)) {
            if (!(el instanceof HTMLElement))
                continue;
            const style = window.getComputedStyle(el);
            const opacity = Number.parseFloat(style.opacity || "1");
            const hasBox = el.getClientRects().length > 0;
            if (!hasBox)
                continue;
            if (style.display === "none")
                continue;
            if (style.visibility === "hidden")
                continue;
            if (opacity === 0)
                continue;
            return true;
        }
        return false;
    };
    const isDesktopNavOpen = getLocalStorage(isNavOpenKey) ?? true;
    const [mobileNavOpened, { toggle: toggleMobileNav }] = useDisclosure(false); // For mobile nav visibility
    const [desktopNavCollapsed, { toggle: toggleDesktopNav }] = useDisclosure(!isDesktopNavOpen); // For desktop nav width
    /**
     * Inactivity Logout Hook
     */
    const { isLocalhost } = useBrowserContext();
    useInactivityLogout({ enabled: !isLocalhost, onSessionChange });
    // Subscriptions
    // We don't need to return the data, error, fetching, etc.. because we handle that in the urqlExchange.ts file.
    usePriorityReferralReceivedSubscription({ pause: false }, (prev, _data) => prev ?? null);
    useWebsiteLeadReceivedSubscription({ pause: false }, (prev, _data) => prev ?? null);
    const me = getLocalStorage(`${import.meta.env.VITE_SESSION_PREFIX}-me-data`);
    const [{ data: subscriptionCountsData }] = useGetSubscriptionCountsQuery();
    // chatRooms.chatUnreadCount is added by the chat feature but the generated
    // type won't include it until codegen runs against the updated backend schema.
    const countsData = subscriptionCountsData;
    const indicatorCounts = {
        chatUnreadCount: countsData?.chatRooms?.chatUnreadCount ?? 0,
        websiteLeadCount: subscriptionCountsData?.websiteLeads?.websiteLeadCount ?? 0,
        priorityReferralCount: subscriptionCountsData?.priorityReferrals?.priorityReferralCount ??
            0
    };
    // This handles the count of priority referrals that are queried, as well as that come in from the subscription.
    // This combines those two counts to get the total count of priority referrals and keeps it updates.
    const handleNavClick = (path) => {
        isUpToSmallScreen && toggleMobileNav();
        navigate(path);
        toggleMobileNav();
    };
    const handleDesktopNavToggle = () => {
        toggleDesktopNav();
        setLocalStorage(isNavOpenKey, !isDesktopNavOpen);
    };
    // Drag handlers for the app-wide drop zone
    // This detects when the user is dragging a file over the app/drop zone
    const handleDragEnter = (e) => {
        if (isBlockingOverlayOpen())
            return;
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        setIsDragging(true);
    };
    const handleDragLeave = (e) => {
        if (isBlockingOverlayOpen())
            return;
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setIsDragging(false);
        }
    };
    const handleDragOver = (e) => {
        if (isBlockingOverlayOpen())
            return;
        e.preventDefault();
        e.stopPropagation();
    };
    const handleDrop = (e) => {
        if (isBlockingOverlayOpen()) {
            // Prevent the browser from opening the file if dropped outside a modal dropzone,
            // but don't stop propagation or trigger the global upload behavior.
            e.preventDefault();
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current = 0;
        setIsDragging(false);
        // If the upload file modal is already open AND we are editing a file, we don't want to handle the drop here.
        if (fileUpload.opened && !!fileUpload.props?.fileInfo)
            return;
        // Handle dropped files here
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            fileUpload.open({ initialFiles: files });
        }
    };
    return (_jsxs(AppShell, { layout: "alt", header: { height: 60 }, navbar: {
            width: desktopNavCollapsed ? 60 : 215,
            breakpoint: "md",
            collapsed: { mobile: !mobileNavOpened }
        }, style: { height: "100dvh" }, onDragEnter: handleDragEnter, onDragLeave: handleDragLeave, onDragOver: handleDragOver, onDrop: handleDrop, children: [isDragging && !fileUpload.opened && !isBlockingOverlayOpen() && (_jsx(Box, { style: {
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "color-mix(in srgb, var(--mantine-color-black) 70%, transparent)",
                    zIndex: 1000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "4px dashed color-mix(in srgb, var(--mantine-color-white) 80%, transparent)",
                    pointerEvents: "none",
                    animation: "pulse 2s infinite"
                }, children: _jsxs(Center, { style: { flexDirection: "column", gap: "1rem" }, children: [_jsx(Box, { style: {
                                width: "80px",
                                height: "80px",
                                border: "4px solid color-mix(in srgb, var(--mantine-color-white) 80%, transparent)",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                animation: "bounce 1s infinite"
                            }, children: _jsx(ArrowDownward, { sx: {
                                    color: "var(--mantine-color-white)",
                                    width: "40px",
                                    height: "40px"
                                } }) }), _jsx(Text, { variant: "body", size: "xl", fw: 700, c: "white", ta: "center", children: "Drop files here to upload" }), _jsx(Text, { variant: "body", size: "sm", c: "gray.4", ta: "center", children: "Drag and drop your files anywhere to upload" })] }) })), _jsx(AppShell.Header
            // className={`${isMasquerading ? styles.pulsingShadow : ""}`} // Uncomment if we want to add the pulsing shadow to the header when masquerading
            , { 
                // className={`${isMasquerading ? styles.pulsingShadow : ""}`} // Uncomment if we want to add the pulsing shadow to the header when masquerading
                className: `${isMasquerading ? "pulsingBackground" : ""}`, style: {
                    alignContent: "center"
                }, children: _jsx(Header, { me: me, opened: mobileNavOpened, toggleMobileNav: toggleMobileNav, navCollapsed: desktopNavCollapsed, toggleDesktopNav: handleDesktopNavToggle, onSessionChange: onSessionChange, onSpotlightOpen: onSpotlightOpen }) }), _jsx(AppShell.Navbar, { style: {
                    paddingTop: "1rem",
                    paddingLeft: "0.5rem",
                    backgroundColor: NAVBAR_BACKGROUND
                }, children: _jsxs(Stack, { style: { overflowX: "clip", height: "100%" }, children: [_jsxs(Flex, { align: "center", justify: "space-between", style: { marginBottom: "2rem" }, children: [_jsx(Image, { src: logoFull, style: {
                                        height: "2.5rem",
                                        minWidth: desktopNavCollapsed
                                            ? "15.5rem"
                                            : "12.5rem",
                                        transition: "min-width 200ms ease"
                                    } }), _jsx(Burger, { opened: mobileNavOpened, hiddenFrom: "md", size: "sm", color: "white", style: { marginRight: "1rem" }, onClick: toggleMobileNav })] }), _jsx(Stack, { style: { flex: 1 }, children: pages.map((item) => {
                                // Show/Hide the item based on user role
                                const upperCaseUserRole = userRole?.toUpperCase();
                                const upperCaseHideFromRoles = item.hideFromRoles?.map((role) => role.toUpperCase());
                                const upperCaseAllowForRoles = item.allowForRoles?.map((role) => role.toUpperCase());
                                // Show/Hide the item based on organization type
                                const oganizationTypes = me?.organization?.organizationTypes ?? [];
                                if (oganizationTypes.length > 0) {
                                    const upperCaseOrganizationTypes = oganizationTypes.map((type) => type.toUpperCase());
                                    const hideFromOrganizationTypes = item.hideFromOrganizationTypes ?? [];
                                    const upperCaseHideFromOrganizationTypes = hideFromOrganizationTypes.map((type) => type.toUpperCase());
                                    // If the user is not logged in, or the item is hidden from the user's organization type, don't show the item
                                    if (!upperCaseOrganizationTypes ||
                                        (upperCaseHideFromOrganizationTypes &&
                                            upperCaseHideFromOrganizationTypes.some((type) => upperCaseOrganizationTypes.includes(type)))) {
                                        return null;
                                    }
                                }
                                // If the user is not logged in, or the item is hidden from the user's role, don't show the item
                                if (!upperCaseUserRole ||
                                    (upperCaseHideFromRoles &&
                                        upperCaseHideFromRoles.includes(upperCaseUserRole))) {
                                    return null;
                                }
                                // If the user is logged in, but the item is not allowed for the user's role, don't show the item
                                if (!upperCaseUserRole ||
                                    (upperCaseAllowForRoles &&
                                        !upperCaseAllowForRoles.includes(upperCaseUserRole))) {
                                    return null;
                                }
                                return (_jsx(Tooltip, { label: item.label, disabled: !desktopNavCollapsed, openDelay: 500, position: "right", offset: 0, transitionProps: {
                                        transition: "slide-right",
                                        duration: 200
                                    }, children: _jsx(Indicator, { color: "var(--mantine-color-red-5)", size: 15, position: !isDesktopNavOpen
                                            ? "top-end"
                                            : "middle-end", offset: !isDesktopNavOpen ? 10 : 25, disabled: !indicatorCounts[item?.indicator ?? ""], label: indicatorCounts[item?.indicator ?? ""], children: _jsx(NavLink, { label: !desktopNavCollapsed ||
                                                mobileNavOpened
                                                ? item.label
                                                : " ", className: "navlink", leftSection: item.icon && (_jsx(item.icon, { sx: {
                                                    width: "1.3rem",
                                                    height: "1.3rem"
                                                } })), style: {
                                                marginBottom: "0.25rem",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden"
                                            }, childrenOffset: !isUpToSmallScreen &&
                                                desktopNavCollapsed
                                                ? 0
                                                : 20, active: location.pathname.startsWith(item.path), onClick: () => {
                                                // If the item has subItems, don't navigate
                                                if (item.subItems)
                                                    return;
                                                handleNavClick(item.path);
                                            }, children: item.subItems?.map((subItem) => (_jsx(Tooltip, { label: subItem.label, disabled: !desktopNavCollapsed, openDelay: 500, position: "right", offset: 0, transitionProps: {
                                                    transition: "slide-right",
                                                    duration: 200
                                                }, children: _jsx(NavLink, { label: subItem.label, className: "navlink", leftSection: subItem.icon && (_jsx(subItem.icon, { sx: {
                                                            width: "1.3rem",
                                                            height: "1.3rem"
                                                        } })), style: {
                                                        marginBottom: "0.25rem",
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden"
                                                    }, active: location.pathname.startsWith(subItem.path), onClick: () => {
                                                        handleNavClick(subItem.path);
                                                    } }, subItem.path) }, subItem.path))) }, item.label) }) }, item.label));
                            }) }), _jsx(Tooltip, { label: "Help Request", disabled: !desktopNavCollapsed, openDelay: 500, position: "right", offset: 0, transitionProps: {
                                transition: "slide-right",
                                duration: 200
                            }, children: _jsx(NavLink, { className: "navlink", label: !desktopNavCollapsed || mobileNavOpened
                                    ? "Help Request"
                                    : " ", leftSection: _jsx(Image, { src: nellyHelp, style: {
                                        width: "2rem",
                                        height: "2rem",
                                        marginLeft: "-0.35rem"
                                    } }), onClick: () => helpRequest.open() }) })] }) }), _jsx(AppShell.Main, { children: 
                // The double stack is to prevent overflow, but still let shadows be visible
                _jsx(Stack, { flex: 1, style: { minHeight: 0, overflow: "hidden" }, children: _jsx(Stack, { flex: 1, style: {
                            minHeight: 0,
                            margin: "1rem",
                            marginBottom: "0.5rem"
                        }, children: _jsx(Outlet, {}) }) }) }), _jsx("style", { children: `
          @keyframes pulse {
            0% {
              border-color: color-mix(in srgb, var(--mantine-color-white) 80%, transparent);
            }
            50% {
              border-color: color-mix(in srgb, var(--mantine-color-white) 40%, transparent);
            }
            100% {
              border-color: color-mix(in srgb, var(--mantine-color-white) 80%, transparent);
            }
          }
          @keyframes bounce {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-10px);
            }
          }
        ` })] }));
}
